import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton, Drawer, Box, Button, Menu, MenuItem,
  useTheme, useMediaQuery, InputBase, Divider, Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import MailIcon from '@mui/icons-material/MailOutline';
import LightIcon from '@mui/icons-material/LightModeOutlined';
import DarkIcon from '@mui/icons-material/DarkModeOutlined';
import PersonIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, LANGUAGE_NAMES } from '../../i18n';
import { useModo } from '../../config/modo';
import { irAIdioma } from '../../config/idioma_url';
import { useLector } from '../../config/lector';
import { ofreceConservacion } from '../../config/sites';
import logo from '../../assets/logo-footer.png';

/**
 * EL MENU.
 *
 * Cuatro entradas, y cada una NOMBRA UN PROPOSITO, no un archivo. Es la decision
 * que decide si el sitio aguanta lo que venga (PROPUESTA_SITIO.md §0): el menu
 * anterior desplegaba las trece entidades una por una, asi que cada voz nueva
 * lo alargaba y cada seccion nueva no tenia donde ir.
 *
 * EL ORDEN LO FIJO FAINA (26-ago) y no es alfabetico ni por trafico: es el
 * orden en que se presenta una casa. Primero quien la abre, despues a quien le
 * habla, despues quien habla, despues lo que hay dentro, y al final las gracias.
 *
 *   La Fundacion            quien abre la puerta
 *   Trabajadores de la Luz  A QUIEN se le habla
 *   Esenios                 QUIEN habla, los que vivieron una vida humana
 *   Angeles y Arcangeles    QUIEN habla, los que no tienen rostro
 *   Biblioteca              lo que hay dentro: las 94 piezas
 *   Agradecimientos         quien lo hizo posible
 *   Entrar                  la cuenta
 *
 * La biblioteca queda en cuarto lugar, no en primero, PERO NO SE ALEJA: la caja
 * de buscar vive en esta misma barra y esta siempre a un gesto — incluida la
 * pantalla del movil, donde el menu se pliega y ella no.
 *
 * TRABAJADORES DE LA LUZ VA EN EL MENU y NO entre las voces (Faina, 26-ago:
 * «son parte fundamental del proyecto»). No es una voz: nadie firma asi. Es el
 * destinatario — Jeshua es quien les habla. Ver src/config/destinatario.js.
 *
 * 🔴 MUSICA NO VA EN EL MENU. Ni en la barra, ni en el desplegable del movil,
 * ni en la lista de secciones del pie: el desplegable del movil ES el menu, y
 * dejarla ahi era no haber entendido la primera vez (Faina lo repitio el
 * 26-ago). Sigue existiendo en /musica-mom, se llega por Spotify desde el pie,
 * y sobre todo vive DENTRO de cada canalizacion —la musica de esa pieza, para
 * escucharla mientras se lee—, que es donde de verdad tiene sentido.
 *
 * Las trece entidades NO estan en el menu: estan dentro de «Las voces» y de la
 * biblioteca, que es donde se eligen mirando, no leyendo una lista.
 *
 * A la derecha, tres controles pequenos y siempre visibles: idioma, claro y
 * oscuro, y entrar. El boton de DONAR vive en el pie y dentro de La Fundacion,
 * nunca aqui: una fundacion que pide dinero en la primera linea de cada pagina
 * se lee como una tienda.
 *
 * EN EL MOVIL el menu se pliega, PERO LA CAJA DE BUSCAR NO SE PLIEGA NUNCA:
 * es la accion principal del sitio.
 */

const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const esMovil = useMediaQuery(theme.breakpoints.down('md'));
  const { esOscuro, alternar } = useModo();
  const { identificado, lector, fijar } = useLector();

  const [abierto, setAbierto] = useState(false);
  const [anclaIdioma, setAnclaIdioma] = useState(null);
  const [busqueda, setBusqueda] = useState('');

  /* 🔴 LA CAJA DE BUSCAR PASA A SER UN ICONO (Faina, 28-ago).
     Era un campo permanente, y a 1000 px —un portatil normal— se encogia a unos
     40: ya no cabia la palabra «Buscar» y quedaba una lupa con un cursor. Un
     campo de texto que se estrecha hasta dejar de parecer un campo es lo peor de
     los dos mundos: ocupa sitio y no dice que se puede escribir dentro.
     Ahora es un icono como el de idioma y el de claro/oscuro: se pulsa y se
     abre. El menu gana ~150 px, los cuatro controles de la derecha quedan
     homogeneos, y sobre todo deja de anunciar «esto es un buscador» — a este
     sitio no se entra buscando, se entra por lo que te duele.
     La busqueda de verdad vive en la Biblioteca; esto es solo la puerta. */
  const [buscando, setBuscando] = useState(false);
  const campo = useRef(null);

  useEffect(() => {
    if (buscando && campo.current) campo.current.focus();
  }, [buscando]);

  /* CONSERVACION (Def 74, 30-ago). Va DESPUES de La Fundacion y ANTES de las
     voces: es la segunda ala de la mision, no una seccion de contenido. Y solo
     aparece en .cl y .es —en el sitio internacional ni el menu ni la ruta
     existen— porque el servicio se presta con la ley de cada pais. */
  const ENTRADAS = [
    { texto: t('menu.fundacion'), ruta: '/nosotros' },
    ...(ofreceConservacion() ? [{ texto: t('menu.conservacion'), ruta: '/conservacion' }] : []),
    { texto: t('menu.tdl'), ruta: '/trabajadores-de-la-luz' },
    { texto: t('menu.esenios'), ruta: '/esenios' },
    { texto: t('menu.angeles'), ruta: '/angeles-y-arcangeles' },
    /* 33 (Faina, 14-sep): lo que el numero ha significado en el mundo y, desde
       ahi, las 33 enseñanzas. «Yo lo pondria a continuacion de angeles y
       arcangeles.» */
    { texto: t('menu.treinta'), ruta: '/33' },
    { texto: t('menu.biblioteca'), ruta: '/canalizaciones' },
    /* CÓMO LEER (Faina, 11-sep): al lado de la biblioteca, porque quien va a
       leer la tiene a mano. Complementa a «Cómo trabajamos». Se llamo «Tips &
       Hints» unas horas; Faina la dejo en español porque era la unica entrada
       del menu en ingles y el sitio se traduce a decenas de idiomas. */
    { texto: t('menu.leer'), ruta: '/como-leer' },
    { texto: t('menu.gracias'), ruta: '/gracias' },
    /* 🚪 Sin intranet (Faina, 07-10-2026): el menú es el mismo para todos. */
  ];

  const activa = (ruta) => location.pathname === ruta
    || (ruta === '/canalizaciones' && location.pathname.startsWith('/canalizacion'))
    || (ruta === '/angeles-y-arcangeles' && location.pathname.startsWith('/angeles-y-arcangeles'));

  const buscar = (e) => {
    e.preventDefault();
    const q = busqueda.trim();
    navigate(q ? `/canalizaciones?q=${encodeURIComponent(q)}` : '/canalizaciones');
    setBusqueda('');
    setAbierto(false);
    setBuscando(false);
  };

  /* 🔴 CAMBIAR DE IDIOMA ES IR A OTRA DIRECCION (28-ago), no solo cambiar un
     estado: desde que el idioma vive en la URL, la pagina alemana ES otra
     pagina. Se va a ella; el idioma se fija solo al llegar.
     Se sigue guardando la eleccion, para que la proxima visita a la portada
     caiga donde toca. → config/idioma_url.js */
  const cambiarIdioma = (lng) => {
    i18n.changeLanguage(lng);
    setAnclaIdioma(null);
    /* 🔴 SI TIENE CUENTA, EL CAMBIO SE GUARDA EN ELLA (28-ago). Desde que el
       idioma de la cuenta manda sobre la direccion, cambiarlo aqui sin guardarlo
       devolveria al lector a su idioma anterior en el acto: un boton que no
       hace nada. Se guarda, y asi le acompaña tambien en el proximo aparato. */
    if (identificado) fijar({ idioma: lng });
    irAIdioma(lng);
  };

  const botonSx = (act) => ({
    color: act ? 'var(--acento)' : 'var(--texto)',
    fontFamily: 'var(--fuente-titulo)',
    fontSize: { md: '0.72rem', lg: '0.84rem' },
    fontWeight: act ? 600 : 400,
    textTransform: 'none',
    px: { md: 0.7, lg: 1 },
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: 'rgba(127,127,127,0.12)' }
  });

  const cajaSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.6,
    py: 0.5,
    borderRadius: 999,
    border: '1px solid var(--linea)',
    background: 'var(--superficie)',
    transition: 'border-color .18s ease',
    '&:focus-within': { borderColor: 'var(--acento)' }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'var(--fondo)',
          borderBottom: '1px solid var(--linea)',
          color: 'var(--texto)'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>

          {/* 🏠 EL LOGO VA PRIMERO, tambien en movil (Faina, 2-sep). Antes el ☰
              quedaba pegado al borde y el logo detras: el patron habitual de
              movil, pero la casa es lo primero que hay que reconocer, asi que
              el logo abre la barra y la hamburguesa va a su lado. */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
            <Box component="img" src={logo} alt={t('menu.nombre_fundacion')}
                 sx={{ height: { xs: 30, sm: 36 }, display: 'block' }} />
          </Box>

          {esMovil && (
            <IconButton onClick={() => setAbierto(true)} sx={{ color: 'var(--texto)' }} aria-label={t('menu.menu')}>
              <MenuIcon />
            </IconButton>
          )}

          {!esMovil && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
              {ENTRADAS.map((e) => (
                <Button key={e.ruta} component={Link} to={e.ruta} sx={botonSx(activa(e.ruta))}>
                  {e.texto}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Cerrado: un icono. Abierto: la caja, con su texto entero. */}
          {buscando ? (
            <Box component="form" onSubmit={buscar}
                 sx={{ ...cajaSx, flexShrink: 0, width: { xs: 180, sm: 250 } }}>
              <SearchIcon sx={{ fontSize: '1.15rem', color: 'var(--texto-suave)' }} />
              <InputBase
                inputRef={campo}
                value={busqueda}
                onChange={(ev) => setBusqueda(ev.target.value)}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') buscar(ev);
                  if (ev.key === 'Escape') { setBusqueda(''); setBuscando(false); }
                }}
                placeholder={t('menu.busca_placeholder')}
                inputProps={{ 'aria-label': t('menu.buscar_aria') }}
                sx={{
                  flex: 1,
                  fontFamily: 'var(--fuente-lectura)',
                  fontSize: '0.9rem',
                  color: 'var(--texto)',
                  '& input::placeholder': { color: 'var(--texto-tenue)', opacity: 1 }
                }}
              />
              {/* El envio con Enter NO estaba llegando: sin un boton de tipo
                  submit dentro, el navegador no garantiza el envio implicito de
                  un formulario. Este boton no se ve ni se tabula; solo existe
                  para que la tecla Enter tenga a quien avisar. */}
              <button type="submit" aria-hidden="true" tabIndex={-1}
                      style={{ position: 'absolute', width: 1, height: 1,
                               opacity: 0, pointerEvents: 'none' }}>{t('menu.buscar')}</button>
            </Box>
          ) : (
            <IconButton onClick={() => setBuscando(true)}
                        sx={{ color: 'var(--texto-suave)' }} aria-label={t('menu.buscar')}>
              <SearchIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          )}

          {/* 🔴 CONTACTO, VISIBLE EN TODAS LAS PAGINAS (Faina, 29-ago:
              «¿contacto no debería tener un icono? ¿no debería estar un poco más
              visible?»). Estaba solo en el pie, y en este sitio esa pagina no es
              un «escríbenos» de cortesia: es por donde un CANALIZADOR da permiso
              sobre su obra y por donde alguien DONA — las dos cosas criticas del
              proyecto entran por ahi.

              ⚠️ Va como icono y no como septima entrada del menu porque no cabe:
              medido, a 1.180 px sobran 252 px, pero a 1.000 —un portatil
              normal— quedan 72 y «Contacto» necesita unos 85. Y en el menu
              ninguna entrada lleva icono: poner uno solo rompería la fila.
              Aqui, junto a la lupa, cabe siempre y es donde se buscan las
              acciones. El sobre no necesita explicacion. */}
          <Tooltip title={t('menu.escribenos')} arrow>
            <IconButton component={Link} to="/contacto"
                        sx={{ color: location.pathname === '/contacto'
                          ? 'var(--acento)' : 'var(--texto-suave)' }}
                        aria-label={t('menu.escribenos')}>
              <MailIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          </Tooltip>

          {/* 🔴 EL GLOBO SOLO APARECE SI HAY DONDE ELEGIR (Faina, 28-ago:
              «¿el icono está bien ahí?»). Con un solo idioma abria un menu de
              UNA opcion, ya marcada: un control que no permite hacer nada y que
              ademas promete algo —«esto esta en varios idiomas»— que hoy no es
              verdad. Es la misma regla que dejo el selector en un solo idioma.
              Vuelve solo en cuanto haya dos. */}
          {SUPPORTED_LANGUAGES.length > 1 && (
            <IconButton onClick={(ev) => setAnclaIdioma(ev.currentTarget)}
                        sx={{ color: 'var(--texto-suave)' }} aria-label={t('lang.select')}>
              <LanguageIcon sx={{ fontSize: '1.2rem' }} />
            </IconButton>
          )}

          <IconButton onClick={alternar} sx={{ color: 'var(--texto-suave)' }}
                      aria-label={esOscuro ? t('menu.modo_claro') : t('menu.modo_oscuro')}>
            {esOscuro ? <LightIcon sx={{ fontSize: '1.2rem' }} /> : <DarkIcon sx={{ fontSize: '1.2rem' }} />}
          </IconButton>

          {esMovil ? (
            <IconButton component={Link} to={identificado ? '/cuenta' : '/entrar'}
                        sx={{ color: 'var(--texto-suave)' }}
                        aria-label={identificado ? t('menu.mi_cuenta') : t('menu.entrar')}>
              <PersonIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          ) : (
            <Button
              component={Link}
              to={identificado ? '/cuenta' : '/entrar'}
              sx={{
                ml: 0.5,
                color: identificado ? 'var(--texto)' : 'var(--acento-texto)',
                background: identificado ? 'transparent' : 'var(--acento)',
                border: identificado ? '1px solid var(--linea)' : 'none',
                fontFamily: 'var(--fuente-titulo)',
                fontSize: { md: '0.82rem', lg: '0.9rem' },
                textTransform: 'none',
                borderRadius: 999,
                px: 2,
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:hover': identificado
                  ? { borderColor: 'var(--texto-tenue)' }
                  : { background: 'var(--acento)', filter: 'brightness(1.08)' }
              }}
            >
              {identificado ? (lector.nombre || t('menu.mi_cuenta')) : t('menu.entrar')}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anclaIdioma}
        open={Boolean(anclaIdioma)}
        onClose={() => setAnclaIdioma(null)}
        PaperProps={{ sx: { maxHeight: 420, background: 'var(--superficie)', color: 'var(--texto)' } }}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <MenuItem key={lng} selected={i18n.language === lng} onClick={() => cambiarIdioma(lng)}
                    sx={{ fontFamily: 'var(--fuente-lectura)', fontSize: '0.92rem' }}>
            {LANGUAGE_NAMES[lng] || lng}
          </MenuItem>
        ))}
      </Menu>

      <Drawer
        anchor="left"
        open={abierto}
        onClose={() => setAbierto(false)}
        PaperProps={{ sx: { width: 288, background: 'var(--fondo)', color: 'var(--texto)' } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={() => setAbierto(false)} sx={{ color: 'var(--texto-suave)' }} aria-label={t('menu.cerrar')}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', px: 1.5, pb: 3 }}>
          {ENTRADAS.map((e) => (
            <Button key={e.ruta} component={Link} to={e.ruta} onClick={() => setAbierto(false)}
                    sx={{ ...botonSx(activa(e.ruta)), justifyContent: 'flex-start', py: 1.4, fontSize: '1.05rem' }}>
              {e.texto}
            </Button>
          ))}
          <Divider sx={{ my: 1.5, borderColor: 'var(--linea)' }} />
          <Button component={Link} to="/contacto" onClick={() => setAbierto(false)}
                  sx={{ ...botonSx(activa('/contacto')), justifyContent: 'flex-start', py: 1.2 }}>
            {t('menu.contacto')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
